import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageTitle } from "../components/PageTitle";
import { registerUser } from "../api/login";
import {
  OK,
  showErrorMessage,
  showSuccessMessage,
  isValidEmail,
} from "../utils";
import { useCartStore } from "../store/cartStore";

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const guestCartId = useCartStore((s) => s.cartId);
  const mergeCartAfterLogin = useCartStore((s) => s.mergeCartAfterLogin);

  useEffect(() => {
    if (localStorage.getItem("Token")) {
      showSuccessMessage("You are already logged in! 🌈");
      navigate("/", { replace: true });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showErrorMessage("Full name is required");
      return;
    }
    if (!email) {
      showErrorMessage("Email is required");
      return;
    }
    if (!isValidEmail(email)) {
      showErrorMessage("Enter a valid email address");
      return;
    }
    if (!/^\d{10}$/.test(mobile)) {
      showErrorMessage("Mobile number must be exactly 10 digits");
      return;
    }
    if (!password) {
      showErrorMessage("Password is required");
      return;
    }
    if (password.length < 6 || password.length > 32) {
      showErrorMessage("Password must be between 6 and 32 characters");
      return;
    }

    try {
      setLoading(true);
      const res = await registerUser({
        name: name.trim(),
        email: email.trim(),
        mobile,
        password,
        guest_cart_id: guestCartId,
      });
      if (res?.status !== OK)
        throw res?.error || res?.message || "Registration failed";

      const token = res?.token || res?.data?.token;
      if (token) {
        localStorage.setItem("Token", token);
        localStorage.setItem("userType", "Customer");
        const newCartId = res?.cart_id || res?.data?.cart_id;
        if (newCartId) await mergeCartAfterLogin(newCartId);
      }

      showSuccessMessage(
        "Account created successfully! Welcome to HueHoppers 🌈",
      );
      navigate("/");
    } catch (err) {
      showErrorMessage(err || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle
        title="Register"
        subtitle="Join the Hop! Your hue journey starts here!"
        bgImage="default.png"
      />

      <section className="flat-spacing-10">
        <div className="container">
          <div className="form-register-wrap">
            <div className="flat-title mb_30 px-0">
              <h5 className="mb_18">Register</h5>
              <p className="text_black-2">
                Sign up for early Sale access plus tailored new arrivals, trends
                and promotions. To opt out, click unsubscribe in our emails.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="tf-field style-1 mb_15">
                <input
                  className="tf-field-input tf-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.trimStart())}
                />
                <label className="tf-field-label">Full Name *</label>
              </div>

              {/* Mobile */}
              <div className="tf-field style-1 mb_15">
                <input
                  className="tf-field-input tf-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                />
                <label className="tf-field-label">Phone Number *</label>
              </div>

              {/* Email */}
              <div className="tf-field style-1 mb_15">
                <input
                  className="tf-field-input tf-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label className="tf-field-label">Email *</label>
              </div>

              {/* Password */}
              <div
                className="tf-field style-1 mb_30"
                style={{ position: "relative" }}
              >
                <input
                  className="tf-field-input tf-input"
                  type={showPassword ? "text" : "password"}
                  maxLength={32}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: 44 }}
                />
                <label className="tf-field-label">Password *</label>
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  style={{
                    position: "absolute",
                    right: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#8a8a8a",
                    opacity: 0.75,
                  }}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M2 12C3.73 8.11 7 5 12 5s8.27 3.11 10 7c-1.73 3.89-5 7-10 7s-8.27-3.11-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="1.4"
                      />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 3L21 21"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10.58 10.58A2 2 0 0 0 13.42 13.42"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                      <path
                        d="M9.36 5.37A10.94 10.94 0 0 1 12 5c5 0 9.27 3.11 11 7-0.59 1.31-1.46 2.5-2.54 3.5M6.61 6.61C4.62 7.93 3.15 9.84 2 12c1.73 3.89 6 7 10 7 1.12 0 2.2-0.16 3.23-0.46"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Submit */}
              <div className="mb_20">
                <button
                  type="submit"
                  disabled={loading}
                  className="tf-btn w-100 btn-fill"
                  style={{
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Creating..." : "Register"}
                </button>
              </div>

              {/* Login link */}
              <div className="text-center">
                <Link to="/login" className="tf-btn btn-line">
                  Already have an account? Log in here
                  <i className="icon icon-arrow1-top-left" />
                </Link>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default Register;
